import React, { useEffect, useState } from "react";
import { Alert, Button, Card, Spin } from "antd";
import FormRenderer from "../forms/FormRenderer";
import { FormElement } from "../../api/formApi";
import { fetchPublicScreeningQuestions, submitScreening } from "../../api/healthcare/ScreeningApi";
import { ScreeningAnswer, ScreeningPass, ScreeningQuestion } from "../../types/appointments/ScreeningTypes";
import { classifyScreeningError } from "../../utils/healthcare/screeningOutcome";

export interface ScreeningStepProps {
	appointmentTypeId: number;
	/** The `?t=` booking-link token, forwarded to the submission so a scoped link stays scoped. */
	bookingToken?: string;
	onPass: (pass: ScreeningPass) => void;
	/** Terminal contraindication. The message is clinic-authored — render it verbatim. */
	onBlocked: (message: string) => void;
	title?: string;
	submitLabel?: string;
}

const toElement = (q: ScreeningQuestion): FormElement => ({
	id: q.id,
	type: q.type,
	label: q.question_text,
	question: q.question_text,
	options: q.options.map((o) => o.label),
	required: q.is_required,
});

/** `FormFieldRenderer` values are option LABELS, so the ids the backend wants come back by lookup. */
const toAnswers = (questions: ScreeningQuestion[], values: Record<string | number, any>): ScreeningAnswer[] =>
	questions.map((q) => {
		const raw = values[q.id];
		const labels = Array.isArray(raw) ? raw : raw === undefined || raw === null || raw === "" ? [] : [raw];
		const option_ids = labels
			.map((label: string) => q.options.find((o) => o.label === label)?.id)
			.filter((id: number | undefined): id is number => typeof id === "number");
		return { question_id: q.id, option_ids };
	});

/**
 * The pre-booking screening questionnaire (S8, ClickUp 86d439wvy). Shared by the public booking
 * page and the patient portal so there is one clinical gate, not two that can drift.
 */
const ScreeningStep: React.FC<ScreeningStepProps> = ({
	appointmentTypeId,
	bookingToken,
	onPass,
	onBlocked,
	title = "A few questions before we book",
	submitLabel = "Continue",
}) => {
	const [questions, setQuestions] = useState<ScreeningQuestion[]>([]);
	const [values, setValues] = useState<Record<string | number, any>>({});
	const [valid, setValid] = useState(false);
	const [loading, setLoading] = useState(true);
	const [submitting, setSubmitting] = useState(false);
	const [notice, setNotice] = useState<string>("");
	const [loadError, setLoadError] = useState<string>("");
	const [reloadKey, setReloadKey] = useState(0);

	useEffect(() => {
		let cancelled = false;

		const load = async () => {
			setLoading(true);
			setLoadError("");
			try {
				const data = await fetchPublicScreeningQuestions(appointmentTypeId);
				if (!cancelled) setQuestions(data?.questions || []);
			} catch (err) {
				console.error("Error loading screening questions:", err);
				if (!cancelled) setLoadError("We could not load the screening questions. Please try again.");
			} finally {
				if (!cancelled) setLoading(false);
			}
		};

		load();
		return () => {
			cancelled = true;
		};
	}, [appointmentTypeId, reloadKey]);

	const handleSubmit = async () => {
		setSubmitting(true);
		setNotice("");

		let result;
		try {
			result = await submitScreening(appointmentTypeId, toAnswers(questions, values), bookingToken);
		} catch (err) {
			// A 422 resolves; only a transport failure lands here.
			console.error("Error submitting screening answers:", err);
			setSubmitting(false);
			setNotice("We could not submit your answers. Please try again.");
			return;
		}

		if (result.ok) {
			onPass(result.pass);
			return;
		}

		if (classifyScreeningError(422, result.error) === "contraindicated") {
			onBlocked(result.message);
			return;
		}

		setSubmitting(false);
		setNotice(result.message || "Please answer every question before continuing.");
	};

	return (
		<div className="selection-section" data-testid="booking-screening-step">
			<h2 className="section-title" style={{ fontSize: "1.2rem" }}>
				{title}
			</h2>
			<p className="section-subtitle" style={{ fontSize: "0.95rem" }}>
				Your answers help us confirm this appointment is right for you.
			</p>

			{notice && <Alert type="warning" showIcon message={notice} style={{ marginBottom: "1rem" }} data-testid="booking-screening-notice" />}

			{loadError ? (
				<Card style={{ textAlign: "center", padding: "1.5rem" }}>
					<p>{loadError}</p>
					<Button onClick={() => setReloadKey((k) => k + 1)}>Try again</Button>
				</Card>
			) : loading ? (
				<div className="loading-state">
					<Spin size="large" />
					<p style={{ marginTop: "1rem" }}>Loading questions...</p>
				</div>
			) : (
				<>
					<FormRenderer
						structure={questions.map(toElement)}
						useSections={false}
						sectionMode="flat"
						values={values}
						onValuesChange={setValues}
						onValidityChange={setValid}
					/>

					<div style={{ marginTop: "1.5rem", textAlign: "center" }}>
						<Button
							type="primary"
							size="middle"
							style={{ fontSize: "1rem", padding: "0.4rem 1.5rem" }}
							data-testid="booking-screening-submit"
							loading={submitting}
							disabled={!valid || submitting}
							onClick={handleSubmit}
						>
							{submitLabel} <i className="fas fa-arrow-right" style={{ marginLeft: "0.5rem" }} />
						</Button>
					</div>
				</>
			)}
		</div>
	);
};

export default ScreeningStep;
